import React, { useEffect, useState } from "react";
import { NavLink, Link } from "react-router-dom";
import {
  Menu,
  X,
  ChevronDown,
  Globe,
  Droplets,
  Wrench,
  ShieldCheck,
  Activity,
  Factory,
  FlaskConical,
} from "lucide-react";
import { AnimatePresence, motion } from "framer-motion";
import logo from "../images/Hydrotec Logo.png";

const mainNavItems = [
  { label: "About", path: "/about" },
  { label: "Projects", path: "/projects" },
];

const topRightItems = [
  { label: "Clients", path: "/clients" },
  { label: "News", path: "/news" },
  { label: "Contact Us", path: "/contact" },
];

const solutionItems = [
  {
    label: "Water ATM Solutions",
    path: "/solutions/water-atm",
    description:
      "Smart public water dispensing systems for cities, campuses, and communities.",
    icon: Droplets,
  },
  {
    label: "Pump Systems",
    path: "/solutions/pumps",
    description:
      "Reliable intake, booster, transfer, and process pump systems.",
    icon: Wrench,
  },
  {
    label: "Water Treatment",
    path: "/solutions/water-treatment",
    description:
      "RO, UF, UV, filtration, chlorination, and integrated purification systems.",
    icon: ShieldCheck,
  },
  {
    label: "Online Monitoring",
    path: "/monitoring",
    description:
      "IoT dashboards for visibility into quality, usage, alerts, and performance.",
    icon: Activity,
  },
  {
    label: "Wastewater Solutions",
    path: "/solutions/wastewater",
    description:
      "Treatment and reuse systems for buildings, institutions, and infrastructure.",
    icon: Factory,
  },
  {
    label: "Chlorination Systems",
    path: "/solutions/chlorination",
    description:
      "Controlled dosing and disinfection systems for safer water quality management.",
    icon: FlaskConical,
  },
];

export default function Navbar() {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [mobileSolutionsOpen, setMobileSolutionsOpen] = useState(false);
  const [megaOpen, setMegaOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    if (!mobileOpen) setMobileSolutionsOpen(false);
  }, [mobileOpen]);

  const closeAllMenus = () => {
    setMobileOpen(false);
    setMobileSolutionsOpen(false);
    setMegaOpen(false);
  };

  const navClass = ({ isActive }) =>
    `relative inline-flex items-center whitespace-nowrap pb-[8px] text-[15px] font-semibold leading-none transition ${
      isActive
        ? "text-[#1f2b33] after:absolute after:left-0 after:bottom-[-8px] after:h-[8px] after:w-[56px] after:rounded-full after:bg-[#86c63f]"
        : "text-[#1f5d92] hover:text-[#143d63]"
    }`;

  return (
    <header
      className={`fixed inset-x-0 top-0 z-50 border-b border-black/5 bg-[#f3f4f5]/95 backdrop-blur-md transition-all duration-300 ${
        scrolled ? "shadow-[0_10px_30px_rgba(0,0,0,0.12)]" : ""
      }`}
      onMouseLeave={() => setMegaOpen(false)}
    >
      <div className="relative mx-auto w-full overflow-visible">
        {/* TOP WAVE AREA */}
        <div className="pointer-events-none absolute left-0 top-0 z-0 h-[64px] w-full overflow-hidden sm:h-[74px] lg:h-[92px]">
          <svg
            viewBox="0 0 1440 150"
            preserveAspectRatio="none"
            className="block h-full w-full"
          >
            <path
              d="
                M0,62
                C120,8 320,2 560,20
                C760,35 980,78 1180,78
                C1295,78 1378,70 1440,62
                L1440,0
                L0,0
                Z
              "
              fill="#114e7b"
            />
            <path
              d="
                M0,82
                C130,28 330,22 570,40
                C770,55 985,98 1185,98
                C1298,98 1380,90 1440,84
                L1440,62
                C1378,70 1295,78 1180,78
                C980,78 760,35 560,20
                C320,2 120,8 0,62
                Z
              "
              fill="#86c63f"
            />
          </svg>
        </div>

        {/* MAIN NAVBAR */}
        <div className="relative z-10 flex h-[74px] items-end bg-transparent sm:h-[82px] lg:h-[104px]">
          <Link
            to="/"
            className="flex h-[56px] min-w-0 shrink-0 items-center justify-start px-4 sm:h-[62px] sm:px-5 lg:h-[72px] lg:min-w-[220px] lg:justify-center lg:border-r lg:border-black/10 lg:px-6 xl:min-w-[270px]"
            onClick={closeAllMenus}
          >
            <img
              src={logo}
              alt="Hydrotec"
              className="h-10 w-auto object-contain sm:h-11 lg:h-14"
            />
          </Link>

          <div className="hidden min-w-0 flex-1 items-stretch lg:flex">
            {/* Left navigation */}
            <div className="flex min-w-0 items-end gap-8 px-6 pb-3 xl:gap-10 xl:px-8">
              <NavLink to="/" className={navClass}>
                Home
              </NavLink>

              <div
                className="relative flex items-end"
                onMouseEnter={() => setMegaOpen(true)}
              >
                <button
                  type="button"
                  className="relative inline-flex items-center gap-2 whitespace-nowrap pb-[8px] text-[15px] font-semibold leading-none text-[#1f5d92] transition hover:text-[#143d63]"
                >
                  <span>Solutions</span>
                  <ChevronDown
                    className={`relative top-[1px] h-4 w-4 transition-transform duration-300 ${
                      megaOpen ? "rotate-180" : ""
                    }`}
                  />
                </button>
              </div>

              {mainNavItems.map((item) => (
                <NavLink key={item.label} to={item.path} className={navClass}>
                  {item.label}
                </NavLink>
              ))}
            </div>

            {/* Right side */}
            <div className="ml-auto flex shrink-0 items-stretch border-l border-black/10">
              <div className="flex items-end gap-6 px-6 pb-3 xl:gap-8 xl:px-8">
                {topRightItems.map((item) => (
                  <NavLink key={item.label} to={item.path} className={navClass}>
                    {item.label}
                  </NavLink>
                ))}
              </div>
            </div>
          </div>

          {/* Mobile */}
          <div className="ml-auto flex h-[56px] items-center px-4 sm:h-[62px] lg:hidden">
            <button
              className="inline-flex h-10 w-10 items-center justify-center rounded-md border border-black/10 bg-white/75 text-[#1f2b33] shadow-sm transition hover:bg-black/[0.04]"
              onClick={() => setMobileOpen((prev) => !prev)}
              aria-label="Toggle navigation"
              type="button"
            >
              {mobileOpen ? (
                <X className="h-5 w-5" />
              ) : (
                <Menu className="h-5 w-5" />
              )}
            </button>
          </div>
        </div>

        {/* FULL-WIDTH MEGA MENU */}
        <AnimatePresence>
          {megaOpen ? (
            <motion.div
              initial={{ opacity: 0, y: 14 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: 10 }}
              transition={{ duration: 0.22 }}
              className="absolute left-0 right-0 top-full hidden lg:block"
              onMouseEnter={() => setMegaOpen(true)}
            >
              <div className="border-t border-black/10 bg-[#f7f8f8] shadow-[0_24px_60px_rgba(0,0,0,0.18)]">
                <div className="grid grid-cols-[1.45fr_0.75fr]">
                  <div className="grid grid-cols-2 gap-2 p-5 xl:p-6">
                    {solutionItems.map((item) => {
                      const Icon = item.icon;
                      return (
                        <Link
                          key={item.label}
                          to={item.path}
                          className="group rounded-xl p-4 transition hover:bg-[#eaf3f9]"
                          onClick={() => setMegaOpen(false)}
                        >
                          <div className="flex items-start gap-3">
                            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-[#114e7b]/10 text-[#114e7b]">
                              <Icon className="h-5 w-5" />
                            </div>
                            <div className="min-w-0">
                              <div className="text-sm font-semibold text-[#1f2b33]">
                                {item.label}
                              </div>
                              <div className="mt-1 text-sm leading-6 text-[#4e5d69]">
                                {item.description}
                              </div>
                            </div>
                          </div>
                        </Link>
                      );
                    })}
                  </div>

                  <div className="border-l border-black/10 bg-[#eef3f1] p-6">
                    <p className="text-[11px] font-semibold uppercase tracking-[0.28em] text-[#6d7b84]">
                      Hydrotec
                    </p>

                    <h3 className="mt-4 text-3xl font-semibold leading-tight tracking-[-0.03em] text-[#1f2b33]">
                      Engineering water infrastructure for modern environments.
                    </h3>

                    <p className="mt-4 text-sm leading-7 text-[#51606b]">
                      Explore integrated access, treatment, monitoring,
                      wastewater, and dosing systems built for real operating
                      conditions.
                    </p>

                    <div className="mt-8 space-y-3">
                      <Link
                        to="/solutions"
                        onClick={() => setMegaOpen(false)}
                        className="inline-flex w-full items-center justify-center border border-[#114e7b] bg-[#114e7b] px-5 py-3 text-sm font-semibold text-white transition hover:bg-[#0d4066]"
                      >
                        View all solutions
                      </Link>

                      <Link
                        to="/contact"
                        onClick={() => setMegaOpen(false)}
                        className="inline-flex w-full items-center justify-center border border-black/10 px-5 py-3 text-sm font-semibold text-[#1f2b33] transition hover:bg-black/[0.04]"
                      >
                        Request consultation
                      </Link>
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          ) : null}
        </AnimatePresence>
      </div>

      {/* Mobile menu */}
      <AnimatePresence>
        {mobileOpen ? (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.22 }}
            className="overflow-hidden border-t border-black/10 bg-[#f3f4f5] shadow-[0_16px_40px_rgba(0,0,0,0.08)] lg:hidden"
          >
            <div className="space-y-2 px-4 py-4 sm:px-5">
              <Link
                to="/"
                onClick={() => setMobileOpen(false)}
                className="block rounded-xl px-4 py-3 text-sm font-medium text-[#1f5d92] transition hover:bg-black/[0.04] hover:text-[#143d63]"
              >
                Home
              </Link>

              <button
                onClick={() => setMobileSolutionsOpen((prev) => !prev)}
                className="flex w-full items-center justify-between rounded-xl px-4 py-3 text-sm font-medium text-[#1f5d92] transition hover:bg-black/[0.04] hover:text-[#143d63]"
                type="button"
              >
                <span>Solutions</span>
                <ChevronDown
                  className={`h-4 w-4 transition-transform ${
                    mobileSolutionsOpen ? "rotate-180" : ""
                  }`}
                />
              </button>

              <AnimatePresence>
                {mobileSolutionsOpen ? (
                  <motion.div
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: "auto" }}
                    exit={{ opacity: 0, height: 0 }}
                    className="overflow-hidden"
                  >
                    <div className="ml-2 space-y-2 border-l border-black/10 pl-3">
                      {solutionItems.map((item) => {
                        const Icon = item.icon;
                        return (
                          <Link
                            key={item.label}
                            to={item.path}
                            onClick={() => {
                              setMobileOpen(false);
                              setMobileSolutionsOpen(false);
                            }}
                            className="block rounded-xl px-4 py-3 transition hover:bg-black/[0.04]"
                          >
                            <div className="flex items-start gap-3">
                              <div className="mt-0.5 text-[#114e7b]">
                                <Icon className="h-4 w-4" />
                              </div>
                              <div>
                                <p className="text-sm font-medium text-[#1f2b33]">
                                  {item.label}
                                </p>
                                <p className="mt-1 text-xs leading-5 text-[#5c6972]">
                                  {item.description}
                                </p>
                              </div>
                            </div>
                          </Link>
                        );
                      })}
                    </div>
                  </motion.div>
                ) : null}
              </AnimatePresence>

              {[...mainNavItems, ...topRightItems].map((item) => (
                <Link
                  key={`${item.label}-${item.path}`}
                  to={item.path}
                  onClick={() => setMobileOpen(false)}
                  className="block rounded-xl px-4 py-3 text-sm font-medium text-[#1f5d92] transition hover:bg-black/[0.04] hover:text-[#143d63]"
                >
                  {item.label}
                </Link>
              ))}

              <button
                className="flex w-full items-center gap-3 rounded-xl px-4 py-3 text-sm font-medium text-[#1f2b33] transition hover:bg-black/[0.04] hover:text-[#143d63]"
                type="button"
              >
                <Globe className="h-4 w-4" />
                <span>EN</span>
              </button>
            </div>
          </motion.div>
        ) : null}
      </AnimatePresence>
    </header>
  );
}